import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Messaging tab — SMS cap for Premium shops, absorb-into-price toggle,
 * and the global SMS provider kill-switch (simulator vs Twilio).
 * Actual Twilio credentials live on the Integrations page, not here.
 */
export const MessagingTab = ({ settings, onChange }) => (
  <div className="panel set-card">
    <h3 className="admin-info-header">
      Messaging
      <span className="plan-sub">SMS caps and how messaging costs are billed to shops</span>
    </h3>
    <div className="set-row">
      <div className="tx"><b>Standard SMS cap</b><small>Max SMS sends per Standard-plan shop per month</small></div>
      <div className="price-inp">
        <input
          type="number" min="0" step="50"
          style={{ width: 90 }}
          value={settings.sms_cap_standard ?? ''}
          onChange={(e) => onChange({ sms_cap_standard: e.target.value })}
          data-testid="settings-sms-cap-standard"
        />
        <span className="pfx" style={{ borderRight: 'none', borderLeft: '1px solid var(--line)' }}>/mo</span>
      </div>
    </div>
    <div className="set-row">
      <div className="tx"><b>Premium SMS cap</b><small>Max SMS sends per Premium-plan shop per month</small></div>
      <div className="price-inp">
        <input
          type="number" min="0" step="50"
          style={{ width: 90 }}
          value={settings.sms_cap_premium}
          onChange={(e) => onChange({ sms_cap_premium: e.target.value })}
          data-testid="settings-sms-cap"
        />
        <span className="pfx" style={{ borderRight: 'none', borderLeft: '1px solid var(--line)' }}>/mo</span>
      </div>
    </div>
    <div className="set-row">
      <div className="tx"><b>Messaging absorbed into price</b><small>Don&apos;t pass-through messaging costs to shops</small></div>
      <button
        type="button"
        className={`tog ${settings.absorb_messaging ? '' : 'off'}`}
        onClick={() => onChange({ absorb_messaging: !settings.absorb_messaging })}
        data-testid="settings-absorb-messaging"
        aria-label="Toggle messaging absorbed"
      />
    </div>
    <div className="set-row">
      <div className="tx">
        <b>Auto-append &quot;Reply STOP&quot; disclosure</b>
        <small>PECR opt-out reminder appended to a customer&apos;s first SMS. Turn off to bake your own opt-out phrase into template copy.</small>
      </div>
      <button
        type="button"
        className={`tog ${settings.sms_append_stop_disclosure !== false ? '' : 'off'}`}
        onClick={() => onChange({ sms_append_stop_disclosure: !(settings.sms_append_stop_disclosure !== false) })}
        data-testid="settings-sms-append-stop"
        aria-label="Toggle STOP disclosure"
      />
    </div>
    <div className="set-row">
      <div className="tx">
        <b>SMS provider</b>
        <small>
          Global kill switch. Simulator logs sends without touching Twilio;
          Twilio actually delivers. Credentials live on the Integrations page.
        </small>
      </div>
      <select
        value={settings.sms_provider || ''}
        onChange={(e) => onChange({ sms_provider: e.target.value || null })}
        data-testid="settings-sms-provider"
        style={{ padding: '6px 10px', border: '1px solid var(--line)', borderRadius: 6, background: 'var(--card)' }}
      >
        <option value="">Env default</option>
        <option value="simulator">Simulator</option>
        <option value="twilio">Twilio</option>
      </select>
    </div>

    {/* SMS wholesale + margin + auto-computed bundle prices.
        The button pulls today's rate from Twilio Pricing API; margin
        is a per-SMS markup; bundle prices auto-recompute (or master
        admin can override any pence value manually below). */}
    <SmsMarginPanel settings={settings} onChange={onChange} />
  </div>
);

/**
 * SMS wholesale rate + margin + auto-derived bundle prices.
 * Lives inside MessagingTab so save() picks up the whole settings
 * object; the "Refresh" button and "Apply margin" button call
 * dedicated backend routes so the write is atomic and returns the
 * new bundle prices immediately.
 */
const fmtGBP = (pence) => `£${(pence / 100).toFixed(2)}`;
const fmtRate = (n) => (n == null ? '—' : `£${Number(n).toFixed(4)}`);
const fmtWhen = (iso) => {
  if (!iso) return 'never';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const SmsMarginPanel = ({ settings, onChange }) => {
  const [pricing, setPricing] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [marginInput, setMarginInput] = useState('');

  const authHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };
  };

  // Parse JSON if response is JSON; fall back to text so `.detail` reads
  // as a human string instead of surfacing a "Unexpected token I" parse
  // error to the operator.
  const readError = async (res) => {
    try {
      const j = await res.clone().json();
      if (j && j.detail) return String(j.detail);
    } catch { /* not json */ }
    const t = await res.text();
    return t.slice(0, 200) || `HTTP ${res.status}`;
  };

  const loadPricing = async () => {
    try {
      const h = await authHeaders();
      const res = await fetch(`${BACKEND_URL}/api/admin/sms/pricing`, { headers: h });
      if (!res.ok) throw new Error(await readError(res));
      const j = await res.json();
      setPricing(j);
      setMarginInput(String(j.margin_per_msg ?? 0.02));
    } catch (e) { setError(e.message); }
  };
  useEffect(() => { loadPricing(); }, []);

  const refresh = async () => {
    setRefreshing(true); setError('');
    try {
      const h = await authHeaders();
      const res = await fetch(`${BACKEND_URL}/api/admin/sms/twilio-price/refresh`, {
        method: 'POST', headers: h,
      });
      if (!res.ok) throw new Error(await readError(res));
      const j = await res.json();
      setPricing({
        price_per_msg: j.price_per_msg,
        unit:          j.unit,
        fetched_at:    j.fetched_at,
        margin_per_msg: j.margin_per_msg,
        bundles:        j.bundles_preview,
      });
    } catch (e) { setError(e.message); } finally { setRefreshing(false); }
  };

  const applyMargin = async ({ propagate }) => {
    setApplying(true); setError('');
    try {
      const h = await authHeaders();
      const res = await fetch(`${BACKEND_URL}/api/admin/sms/margin`, {
        method: 'PUT', headers: h,
        body: JSON.stringify({ margin_per_msg: Number(marginInput), propagate }),
      });
      if (!res.ok) throw new Error(await readError(res));
      const j = await res.json();
      // Sync back into the parent's `settings` so the manual pence
      // inputs re-render with the new values and `save()` won't try
      // to overwrite them with stale numbers.
      if (propagate && j.bundles) {
        onChange({
          sms_margin_per_msg:    j.margin_per_msg,
          sms_bundle_200_price:  j.bundles['200'],
          sms_bundle_400_price:  j.bundles['400'],
          sms_bundle_1000_price: j.bundles['1000'],
        });
      } else {
        onChange({ sms_margin_per_msg: j.margin_per_msg });
      }
      await loadPricing();
    } catch (e) { setError(e.message); } finally { setApplying(false); }
  };

  const rate = pricing?.price_per_msg;
  const margin = Number(marginInput || 0);
  const effective = (rate != null ? rate : 0) + margin;

  return (
    <div style={{ marginTop: 8, padding: '8px 0 0', borderTop: '1px solid var(--line)' }}>
      <div className="set-row">
        <div className="tx">
          <b>SMS bundle prices</b>
          <small>Wholesale Twilio rate + your per-SMS margin drive the three top-up prices</small>
        </div>
      </div>

      {/* Wholesale rate + refresh */}
      <div className="set-row" style={{ paddingLeft: 20, alignItems: 'flex-start' }}>
        <div className="tx">
          <b>Twilio wholesale rate (GB)</b>
          <small>
            Highest carrier price (mobile + local number types). Fetched on demand only.
            {' '}Last refreshed: <b>{fmtWhen(pricing?.fetched_at)}</b>
          </small>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span data-testid="sms-wholesale-rate" style={{ fontWeight: 700, fontSize: 15 }}>
            {fmtRate(rate)}<span style={{ color: 'var(--mute)', fontSize: 12, marginLeft: 4 }}>/ SMS</span>
          </span>
          <button
            type="button"
            onClick={refresh}
            disabled={refreshing}
            data-testid="sms-wholesale-refresh"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '5px 10px', borderRadius: 6,
              border: '1px solid var(--line)', background: 'var(--card)',
              cursor: refreshing ? 'wait' : 'pointer', fontSize: 12,
            }}
          >
            <RefreshCw size={12} style={refreshing ? { animation: 'spin 1s linear infinite' } : undefined} />
            {refreshing ? 'Fetching…' : 'Refresh from Twilio'}
          </button>
        </div>
      </div>

      {/* Margin input */}
      <div className="set-row" style={{ paddingLeft: 20 }}>
        <div className="tx">
          <b>Add margin (per SMS)</b>
          <small>Retail price to shops = wholesale + margin per message</small>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="price-inp">
            <span className="pfx">£</span>
            <input
              type="number" min="0" step="0.001"
              style={{ width: 90 }}
              value={marginInput}
              onChange={(e) => setMarginInput(e.target.value)}
              data-testid="sms-margin-input"
            />
          </div>
          <button
            type="button"
            onClick={() => applyMargin({ propagate: true })}
            disabled={applying || rate == null}
            data-testid="sms-margin-apply"
            title={rate == null ? 'Refresh Twilio rate first' : 'Recompute all three bundle prices'}
            style={{
              padding: '5px 10px', borderRadius: 6, border: '1px solid var(--line)',
              background: rate == null ? 'var(--bg2)' : 'var(--acc, #6366f1)',
              color: rate == null ? 'var(--mute)' : '#fff',
              cursor: applying ? 'wait' : (rate == null ? 'not-allowed' : 'pointer'),
              fontSize: 12, fontWeight: 600,
            }}
          >
            {applying ? 'Applying…' : 'Apply & recompute bundles'}
          </button>
        </div>
      </div>

      {/* Effective retail rate preview */}
      <div style={{ paddingLeft: 20, marginTop: 4, fontSize: 12, color: 'var(--mute)' }}>
        Effective retail rate: <b style={{ color: 'var(--ink)' }}>{fmtRate(effective)}</b> per SMS
        {rate != null && margin > 0 && (
          <> — {`${((margin / rate) * 100).toFixed(0)}% markup on wholesale`}</>
        )}
      </div>

      {error && (
        <div style={{ paddingLeft: 20, marginTop: 6, color: 'var(--red)', fontSize: 12 }} data-testid="sms-margin-error">
          {error}
        </div>
      )}

      {/* Manual override editors (still work). Live-derived when the
          user Applies margin above; type any pence to override. */}
      <div style={{ paddingLeft: 20, marginTop: 10, fontSize: 11, color: 'var(--mute)' }}>
        Bundle prices below auto-fill from wholesale + margin above, but you can override each manually.
      </div>
      {[
        ['200',  'sms_bundle_200_price',  15],
        ['400',  'sms_bundle_400_price',  28],
        ['1000', 'sms_bundle_1000_price', 60],
      ].map(([qty, key, def]) => {
        const pence = settings[key] != null ? settings[key] : Math.round(def * 100);
        const perSms = Number(qty) > 0 ? (pence / 100) / Number(qty) : 0;
        return (
          <div className="set-row" key={key} style={{ paddingLeft: 20 }}>
            <div className="tx">
              <b>{qty} SMS</b>
              <small>{fmtGBP(pence)} · £{perSms.toFixed(3)}/SMS</small>
            </div>
            <div className="price-inp">
              <span className="pfx">£</span>
              <input
                type="number" min="0" step="0.01"
                style={{ width: 90 }}
                value={(pence / 100).toFixed(2)}
                onChange={(e) => onChange({ [key]: Math.max(0, Math.round(Number(e.target.value) * 100)) })}
                data-testid={`settings-${key}`}
              />
            </div>
          </div>
        );
      })}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

/**
 * Feature flags tab — toggles for platform-wide behaviour. Currently
 * one flag (self-serve signup); more will land here as they arrive.
 */
export const FlagsTab = ({ settings, onChange }) => (
  <div className="panel set-card">
    <h3 className="admin-info-header">
      Feature flags
      <span className="plan-sub">Toggle platform-wide behaviour on or off</span>
    </h3>
    <div className="set-row">
      <div className="tx"><b>Self-serve signup</b><small>Allow new shops to sign up on the public marketing site</small></div>
      <button
        type="button"
        className={`tog ${settings.signups_open ? '' : 'off'}`}
        onClick={() => onChange({ signups_open: !settings.signups_open })}
        data-testid="settings-signups-open"
        aria-label="Toggle self-serve signup"
      />
    </div>
    <div className="set-row">
      <div className="tx">
        <b>Enforce subscription gate</b>
        <small>
          Block shop admin writes and hide public booking pages when a subscription is cancelled,
          unpaid, or past due beyond the grace window. Turn on for go-live.
        </small>
      </div>
      <button
        type="button"
        className={`tog ${settings.enforce_subscription_gate ? '' : 'off'}`}
        onClick={() => onChange({ enforce_subscription_gate: !settings.enforce_subscription_gate })}
        data-testid="settings-enforce-subgate"
        aria-label="Toggle subscription gate"
      />
    </div>
    <div className="set-row">
      <div className="tx">
        <b>Past-due grace window</b>
        <small>Days a shop stays accessible after a failed subscription payment before being blocked</small>
      </div>
      <div className="price-inp">
        <input
          type="number" min="0" max="30" step="1"
          style={{ width: 90 }}
          value={settings.past_due_grace_days ?? 7}
          onChange={(e) => onChange({ past_due_grace_days: e.target.value })}
          data-testid="settings-past-due-grace"
        />
        <span className="pfx" style={{ borderRight: 'none', borderLeft: '1px solid var(--line)' }}>days</span>
      </div>
    </div>
  </div>
);
